import React, { useState, useEffect } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import "../../Component/Academic.css"
import { BiBook, BiLaptop, BiGraduation, BiSmile, BiPuzzle, BiCheckCircle, BiClipboard, BiAward, BiCalculator, BiMicrophone, BiBookOpen, BiAtom, BiRocket } from 'react-icons/bi';
import { FaRainbow } from 'react-icons/fa';

const Academics = () => {
  const [activeTab, setActiveTab] = useState('kindergarten');
  const [modalOpen, setModalOpen] = useState(false);
  const [pdfUrl, setPdfUrl] = useState('');

  useEffect(() => {
    AOS.init({
      duration: 800,
      easing: 'ease-in-out',
      once: false,
      mirror: true
    });
  }, []);

  // Data
  const kindergartenSchedule = [
    { time: '9:00-9:30', monday: 'Circle Time', wednesday: 'Story Time', friday: 'Music & Movement' },
    { time: '9:30-10:15', monday: 'Phonics', wednesday: 'Numeracy', friday: 'Art & Craft' },
    { time: '10:15-11:00', monday: 'Outdoor Play', wednesday: 'Science Exploration', friday: 'Dramatic Play' }
  ];
  
  const primarySchedule = [
    { time: '8:30-9:15', monday: 'English', wednesday: 'Math', friday: 'Science' },
    { time: '9:15-10:00', monday: 'Math', wednesday: 'EVS', friday: 'Language Lab' },
    { time: '10:00-10:45', monday: 'PE', wednesday: 'Art', friday: 'Project Work' }
  ];
  
  const middleSchoolSchedule = [
    { time: '8:00-8:45', monday: 'Math', wednesday: 'Science', friday: 'English' },
    { time: '8:45-9:30', monday: 'Social Studies', wednesday: 'Computer', friday: 'Math Lab' },
    { time: '9:30-10:15', monday: 'Second Language', wednesday: 'Olympiad Prep', friday: 'Research Club' }
  ];
  
  const highSchoolSchedule = [
    { time: '7:30-8:15', monday: 'Physics', wednesday: 'Chemistry', friday: 'Math' },
    { time: '8:15-9:00', monday: 'Computer Science', wednesday: 'Biology', friday: 'Economics' },
    { time: '9:00-9:45', monday: 'Test Prep', wednesday: 'Career Counseling', friday: 'University Prep' }
  ];
  
  const assessmentTypes = [
    {
      title: "Formative Assessments",
      icon: "clipboard2-check",
      description: "Ongoing evaluations to monitor learning progress",
      details: [
        "Weekly quizzes",
        "Class participation",
        "Project work",
        "Teacher observations"
      ]
    },
    {
      title: "Summative Assessments",
      icon: "clipboard2-data",
      description: "Periodic evaluations to measure achievement",
      details: [
        "Term-end exams",
        "Standardized tests",
        "Portfolio reviews",
        "Skill demonstrations"
      ]
    },
    {
      title: "Olympiad Preparations",
      icon: "award",
      description: "Special training for competitive exams",
      details: [
        "Monthly mock tests",
        "Problem-solving drills",
        "National level practice",
        "Concept mastery checks"
      ]
    }
  ];
  
  const facilities = [
    {
      title: "Smart Classrooms",
      icon: "smartwatch",
      description: "Interactive digital boards with multimedia content for engaging lessons"
    },
    {
      title: "Science Labs",
      icon: "biotech",
      description: "Fully equipped labs for physics, chemistry and biology experiments"
    },
    {
      title: "Digital Library",
      icon: "book",
      description: "10,000+ books with online resources and reading spaces"
    },
    {
      title: "Math Lab",
      icon: "calculator",
      description: "Hands-on learning with manipulatives and models"
    },
    {
      title: "Language Lab",
      icon: "mic",
      description: "Audio-visual systems for language acquisition"
    },
    {
      title: "Olympiad Center",
      icon: "award",
      description: "Dedicated space for competitive exam preparation"
    }
  ];

  const openPdfModal = (url) => {
    setPdfUrl(url);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setPdfUrl('');
  };

  const CurriculumLevel = ({ title, icon, features, schedule }) => (
    <div className="row g-4">
      <div className="col-lg-5 " data-aos="fade-right">
        <div className="glass-card shadow-sm p-4 p-lg-5 rounded-4 h-100" style={{borderRadius:"10px"}}>
          <h3 className="h4 fw-bold mb-4">
            {icon}
            {title}
          </h3>
          <ul className="list-unstyled mb-4">
            {features.map((feature, index) => (
              <li className="d-flex mb-3" key={index}>
                <BiCheckCircle className="text-primary me-2" size={20} />
                <span>{feature}</span>
              </li>
            ))}
          </ul>
          <button 
            className="btn btn-outline-primary rounded-pill px-4"
            onClick={() => openPdfModal('https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf')}
          >
            View Syllabus PDF
          </button>
        </div>
      </div>
      <div className="col-lg-7 " data-aos="fade-left">
        <div className="glass-card shadow-sm p-4 rounded-4 h-100" style={{borderRadius:"10px"}}>
          <h4 className="h5 text-center fw-bold mb-3">Weekly Schedule</h4>
          <div className="table-responsive">
            <table className="table table-border table-hover align-middle">
              <thead>
                <tr className='table-light'>
                  <th>Time</th>
                  <th>Monday</th>
                  <th>Wednesday</th>
                  <th>Friday</th>
                </tr>
              </thead>
              <tbody>
                {schedule.map((item, index) => (
                  <tr key={index}>
                    <td>{item.time}</td>
                    <td>{item.monday}</td>
                    <td>{item.wednesday}</td>
                    <td>{item.friday}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="programs-page">
      {/* Hero Section */}
      <section className="Subpage_Hero">
        <div className="container">
          <div className="text-lg-start" data-aos="fade-up">
            <h1>Our Academic</h1>
            <p>
              This page must stand out with icons, infographics, student videos, and success stories. 
            </p>
          </div>
        </div>
      </section>
      
      <section className="py-6 position-relative overflow-hidden">
        {/* Background Elements */}
        <div className="position-absolute top-0 start-0 w-100 h-100 opacity-10 z-0">
          <div className="position-absolute top-0 end-0" style={{
            width: "400px",
            height: "400px",
            // background: "radial-gradient(circle, rgba(13,110,253,0.1), rgba(255,255,255,0) 70%)"
          }}></div>
        </div>

        <div className="container position-relative z-1">
          {/* Section Header */}
          <div className="text-center mb-5" data-aos="fade-up">
            <h2 className="display-5 fw-bold mt-4 pt-3 mb-3" style={{color:"#200D46"}}>Our Progressive Curriculum</h2>
            <p className="lead text-muted max-w-800 mx-auto">
              Tailored learning journeys for every developmental stage
            </p>
          </div>

          {/* Level Tabs */}
          <div className="row justify-content-center mb-5" data-aos="fade-up">
            <div className="col-lg-10">
              <div className="d-flex flex-wrap btn  rounded-pill px-4     justify-content-center curriculum-tabs  ">
                {[
                  { id: 'kindergarten', name: 'Kindergarten', icon: <FaRainbow size={20} /> },
                  { id: 'primary', name: 'Primary', icon: <BiBook size={20} /> },
                  { id: 'middle', name: 'Middle School', icon: <BiLaptop size={20} /> },
                  { id: 'highschool', name: 'High School', icon: <BiClipboard size={20} /> }
                ].map((tab) => (
                  <button
                    key={tab.id}
                    className={`tab-button custom-hover  shadow-sm me-2 ${activeTab === tab.id ? 'active bgcolor1 text-white' : ''}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.icon}
                    <span className="ms-2">{tab.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Tab Content */}
          <div className="tab-content" data-aos="fade-up" data-aos-delay="100">
            {/* Kindergarten */}
            {activeTab === 'kindergarten' && (
              <div className="row g-4">
                <div className="col-lg-6 " data-aos="fade-right">
                  <div className="glass-card shadow-sm p-4 p-lg-5 rounded-4 h-100" style={{borderRadius:"10px"}}>
                    <h3 className="h4 border-bottom fw-bold mb-4">
                      <BiSmile className="text-primary  me-3" size={24} />
                      Playway (UKG) Curriculum
                    </h3>
                    <ul className="list-unstyled  mb-4">
                      <li className="d-flex mb-3">
                        <BiCheckCircle className="text-primary me-2" size={20} />
                        <span>Activity-based learning</span>
                      </li>
                      <li className="d-flex mb-3">
                        <BiCheckCircle className="text-primary me-2" size={20} />
                        <span>Phonics and early literacy</span>
                      </li> 
                      <li className="d-flex mb-3">
                        <BiCheckCircle className="text-primary me-2" size={20} />
                        <span>Numeracy through play</span>
                      </li>
                      <li className="d-flex">
                        <BiCheckCircle className="text-primary me-2" size={20} />
                        <span>Social-emotional development</span>
                      </li>
                    </ul>
                    <button 
                      className="btn btn-outline-primary rounded-pill px-4"
                      onClick={() => openPdfModal('https://www.w3.org/WAI/ER/tests/xhtml/testfiles/resources/pdf/dummy.pdf')}
                    >
                      Download Sample Lesson
                    </button>
                  </div>
                </div>
                <div className="col-lg-6 " data-aos="fade-left" >
                <div className="glass-card shadow-sm p-4 rounded-4 h-100" style={{borderRadius:"10px"}}>
                    <h4 className="h5 fw-bold mb-4 text-center">Weekly Schedule</h4>
                    <div className="table-responsive">
                    <table className="table table-border table-hover align-middle">
                        <thead className="table-light">
                        <tr>
                            <th className="text-center">Time</th>
                            <th className="text-center">Monday</th>
                            <th className="text-center">Wednesday</th>
                            <th className="text-center">Friday</th>
                        </tr>
                        </thead>
                        <tbody>
                        {kindergartenSchedule.map((item, index) => (
                            <tr key={index}>
                            <td className="text-start text-nowrap">{item.time}</td>
                            <td className="text-center text-nowrap">{item.monday}</td>
                            <td className="text-center ">{item.wednesday}</td>
                            <td className="text-center ">{item.friday}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                    </div>
                </div>
                </div>
              </div>
            )}

            {/* Primary */}
            {activeTab === 'primary' && (
              <CurriculumLevel 
                title="Primary School Curriculum"
                icon={<BiBook className="text-primary me-3" size={24} />}
                features={[
                  "XSEED Prime methodology",
                  "STEM integrated learning",
                  "Project-based assessments",
                  "Language immersion"
                ]}
                schedule={primarySchedule}
              />
            )}

            {/* Middle */}
            {activeTab === 'middle' && (
              <CurriculumLevel 
                title="Middle School Program"
                icon={<BiLaptop className="text-primary me-3" size={24} />}
                features={[
                  "Subject specialization",
                  "Research skill development",
                  "Olympiad preparation",
                  "Career awareness"
                ]}
                schedule={middleSchoolSchedule}
              />
            )}

            {/* High School */}
            {activeTab === 'highschool' && (
              <CurriculumLevel 
                title="High School Curriculum"
                icon={<BiRocket className="text-primary me-3" size={24} />}
                features={[
                  "Board exam preparation",
                  "Advanced placement courses",
                  "Career counseling",
                  "University readiness"
                ]}
                schedule={highSchoolSchedule}
              />
            )}
          </div>

          {/* Assessment Section */}
          <div className="mt-6 pt-5" data-aos="fade-up">
            <h2 className="text-center mb-5 fw-bold" style={{color:"#200D46"}}>Our Assessment Approach</h2>
            <div className="row g-4">
  {assessmentTypes.map((type, index) => (
    <div className="col-md-4" key={index} data-aos="fade-up" data-aos-delay={index * 100}>
      <div 
        className="modern-card p-4 h-100 transition-all" 
        style={{
          borderRadius: "16px",
          background: "rgba(255, 255, 255, 0.1)",
          border: "1px solid rgba(255, 255, 255, 0.2)",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
          transition: "all 0.3s ease",
          cursor: "pointer"
        }}
        onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-8px)"}
        onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
      >
        {/* Icon with gradient background */}
        <div 
          className="icon-box-lg mb-3 mx-auto d-flex align-items-center justify-content-center rounded-3"
          style={{
            background: "linear-gradient(135deg, #6366f1, #a855f7)",
            width: "56px",
            height: "56px"
          }}
        >
          <BiRocket size={24} className='text-white' />
        </div>

        <h4 className="h5 fw-bold mb-2 text-center text-dark">{type.title}</h4>
        <p className="text-secondary mb-3">{type.description}</p>

        <ul className="list-unstyled mt-3">
          {type.details.map((detail, i) => (
            <li key={i} className="d-flex align-items-center mb-2">
              <BiCheckCircle 
                className="me-2" 
                size={18} 
                style={{ color: "#6366f1" }} 
              />
              <span className="text-muted">{detail}</span>
            </li>
          ))}
        </ul>

        {/* Animated hover effect */}
        <div 
          className="hover-underline"
          style={{
            height: "2px",
            background: "linear-gradient(90deg, #6366f1, #a855f7)",
            width: "10%",
            transition: "width 0.3s ease"
          }}
        ></div>
      </div>
    </div>
  ))}
</div>
          </div>

          {/* Facilities Section */}
          <div className="mt-6 pt-5" data-aos="fade-up">
  <div className="container">
    {/* Modern Header with Gradient Text */}
    <h2 className="text-center mb-5 fw-bold" style={{color:"#200D46"}}>

      Our Learning Facilities
    </h2>

    <div className="row g-4 mb-5 pt-3 p-2">
      {facilities.map((facility, index) => (
        <div className="col-lg-4 col-md-6 " key={index} data-aos="fade-up" data-aos-delay={index * 100}>
          {/* Advanced Card with 3D Effect */}
          <div 
            className="modern-edu-card p-4 h-100 position-relative overflow-hidden shadow-sm"
            style={{
              borderRadius: "20px",
              background: "rgba(255, 255, 255, 0.9)",
              boxShadow: "0 10px 30px rgba(32, 13, 70, 0.1)",
              border: "1px solid rgba(209, 213, 219, 0.3)",
              transition: "all 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
              zIndex: 1
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-10px)";
              e.currentTarget.style.boxShadow = "0 15px 40px rgba(32, 13, 70, 0.2)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 10px 30px rgba(32, 13, 70, 0.1)";
            }}
          >
            {/* Floating Gradient Background (Hidden by default) */}
            <div 
              className="position-absolute top-0 start-0 w-100 h-100 opacity-0"
              style={{
                background: "radial-gradient(circle at center, rgba(109, 40, 217, 0.1) 0%, transparent 70%)",
                transition: "opacity 0.4s ease",
                zIndex: -1
              }}
            />

            {/* Icon with Floating Animation */}
            <div 
              className="icon-box-lg mb-4 mx-auto d-flex align-items-center justify-content-center rounded-3"
              style={{
                width: "60px",
                height: "60px",
                background: "linear-gradient(135deg, #6D28D9, #8B5CF6)",
                transform: "translateY(0)",
                transition: "transform 0.4s ease"
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = "translateY(-5px)"}
              onMouseLeave={(e) => e.currentTarget.style.transform = "translateY(0)"}
            >
              {index === 0 && <BiLaptop size={28} className="text-white" />}
              {index === 1 && <BiAtom size={28} className="text-white" />}
              {index === 2 && <BiBookOpen size={28} className="text-white" />}
              {index === 3 && <BiCalculator size={28} className="text-white" />}
              {index === 4 && <BiMicrophone size={28} className="text-white" />}
              {index === 5 && <BiAward size={28} className="text-white" />}
            </div>

            {/* Content */}
            <h4 className="h4 fw-bold text-center mb-3" style={{color: "#200D46"}}>{facility.title}</h4>
            <p className="text-muted text-center  mb-0" style={{lineHeight: "1.6"}}>{facility.description}</p>

            {/* Animated Underline */}
            <div 
              className="position-absolute bottom-0 start-0 w-0 h-1 bg-gradient-to-r from-purple-500 to-pink-500"
              style={{
                transition: "width 0.4s ease",
              }}
            />
          </div>
        </div>
      ))}
    </div>
  </div>

  {/* Global Hover Effects (Add to your CSS) */}
  <style jsx global>{`
    .modern-edu-card:hover {
      .position-absolute[style*="radial-gradient"] {
        opacity: 1 !important;
      }
      .h-1 {
        width: 100% !important;
      }
    }
  `}</style>
</div>
        </div>

        {/* PDF Modal */}
        {modalOpen && (
          <div className="modal-overlay" onClick={closeModal}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={closeModal}>
                &times;
              </button>
              <iframe 
                src={`https://docs.google.com/gview?url=${pdfUrl}&embedded=true`} 
                style={{ width: '100%', height: '80vh', border: 'none' }}
                title="PDF Viewer"
              ></iframe>
              <div className="text-center mt-3">
                <a 
                  href={pdfUrl} 
                  className="btn btn-primary"
                  target="_blank" 
                  rel="noopener noreferrer"
                  download
                >
                  Download Syllabus
                </a>
              </div>
            </div>
          </div>
        )}

        {/* Custom Styles */}

      </section>
    </div>
  );
};

export default Academics;